import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Logo } from '@/components/Logo';
import ThemeToggle from '@/components/ThemeToggle';
import { useAuth } from '@/contexts/AuthContext';
import {
  Menu,
  X,
  Home,
  Image as ImageIcon,
  Mic,
  FileText,
  User,
  CreditCard,
  LogOut,
  Sparkles,
  Crown,
  Library,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface MobileNavProps {
  onAuthClick?: (type: 'login' | 'register') => void;
}

const MobileNav = ({ onAuthClick }: MobileNavProps) => {
  const [open, setOpen] = useState(false);
  const { user, signOut } = useAuth();
  const location = useLocation();

  const navItems = [
    { title: 'Home', url: '/', icon: Home },
    { title: 'Dashboard', url: '/dashboard', icon: Sparkles },
    { title: 'Images', url: '/dashboard?tab=image', icon: ImageIcon },
    { title: 'Voice', url: '/dashboard?tab=voice', icon: Mic },
    { title: 'Scripts', url: '/dashboard?tab=script', icon: FileText },
    { title: 'Library', url: '/dashboard?tab=library', icon: Library },
  ];

  const accountItems = [
    { title: 'Profile', url: '/profile', icon: User },
    { title: 'Billing', url: '/billing', icon: CreditCard },
  ];

  const handleNavClick = (url: string) => {
    setOpen(false);
    if (url.includes('?')) {
      window.location.href = url;
    }
  };

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path === '/dashboard' && location.pathname === '/dashboard' && !location.search) return true;
    if (path.includes('?tab=') && location.search.includes(path.split('?tab=')[1])) return true;
    return location.pathname === path;
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden">
          <Menu className="h-6 w-6" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[300px] p-0 bg-background/95 backdrop-blur-xl">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 border-b border-border/20">
            <div className="flex items-center justify-between">
              <Logo size="sm" />
              <ThemeToggle />
            </div>
          </div>

          {/* User info */}
          {user && (
            <div className="p-4 border-b border-border/20 bg-gradient-to-r from-purple-500/10 to-blue-500/10">
              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12 ring-2 ring-primary/20">
                  <AvatarImage src={user.avatarUrl} />
                  <AvatarFallback className="bg-gradient-to-br from-purple-600 to-blue-600 text-white font-bold">
                    {user.name?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold text-foreground">{user.name}</p>
                  <Badge className={`text-xs ${user.plan !== 'free' ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white' : ''}`}>
                    {user.plan === 'pro' && <Crown className="w-3 h-3 mr-1" />}
                    {(user.plan || 'free').toUpperCase()}
                  </Badge>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 overflow-auto p-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.title}
                to={item.url}
                onClick={() => handleNavClick(item.url)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive(item.url)
                    ? 'bg-gradient-to-r from-purple-500/20 to-blue-500/20 text-foreground shadow-sm border border-purple-500/30'
                    : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.title}</span>
              </Link>
            ))}

            {user && (
              <>
                <div className="pt-4 pb-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4">
                    Account
                  </p>
                </div>
                {accountItems.map((item) => (
                  <Link
                    key={item.title}
                    to={item.url}
                    onClick={() => setOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                      isActive(item.url)
                        ? 'bg-gradient-to-r from-purple-500/20 to-blue-500/20 text-foreground'
                        : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.title}</span>
                  </Link>
                ))}
              </>
            )}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-border/20">
            {user ? (
              <Button
                variant="ghost"
                onClick={() => {
                  signOut();
                  setOpen(false);
                }}
                className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-500/10"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Sign Out
              </Button>
            ) : (
              <div className="space-y-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    onAuthClick?.('login');
                    setOpen(false);
                  }}
                  className="w-full"
                >
                  Sign In
                </Button>
                <Button
                  onClick={() => {
                    onAuthClick?.('register');
                    setOpen(false);
                  }}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                >
                  Get Started
                </Button>
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileNav;
